/* PP SIZE CHART — open the guide in a dialog instead of a new tab.
 *
 * Progressive enhancement on purpose: the trigger is a real <a href="/pages/size-guide">. Without
 * this script it navigates, exactly as it does today. With the script, the click is intercepted and
 * the same content opens over the product — the shopper keeps the variant they had selected.
 *
 * Uses the native <dialog>, so focus trapping, Escape-to-close, inertness of the page behind and
 * the backdrop all come from the browser rather than from hand-written focus code.
 */
(() => {
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;
  const ZOOM_SPEED = 0.002;
  const ZOOM_STEP = 1.25;
  const ZOOM_EPSILON = 0.000001;
  const desktopViewer = window.matchMedia('(min-width: 750px)');
  const preparedZoomButtons = new WeakSet();
  const preparedZoomDialogs = new WeakSet();
  const preparedZoomRanges = new WeakSet();
  const activeZoomImages = new WeakMap();
  const dragStates = new WeakMap();
  const suppressedZoomClicks = new WeakSet();

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  /* Move every picture inside a PAGE-sourced chart into the same zoom button the metafield-image
   * path renders in Liquid, so both paths run on ONE mechanism — one click handler, one set of CSS
   * rules, one meaning for `is-zoomed`. This shop's charts all arrive through the page path (a
   * page whose body is a photo of a measurements table), so before this the zoom existed only on
   * the branch nobody uses.
   *
   * WHY A REAL <button> RATHER THAN tabindex ON THE IMAGE. Keyboard operability was the original
   * bug on the other path, and an <img tabindex="0"> does not fire a click on Enter or Space —
   * making it work would mean hand-writing key handling, focus styling and pressed state, i.e. a
   * second implementation of what a <button> already does. The button also hands `event.detail`
   * 0 for a keyboard-synthesised click, which is what the zoom handler reads to centre the zoom
   * instead of aiming at coordinates a keyboard never supplied.
   *
   * WHY AT RUNTIME. The markup is the merchant's own page content. Rewriting it in Liquid to slip a
   * wrapper around each <img> means re-emitting HTML this theme does not own; moving the node in
   * the DOM afterwards changes nothing that is stored.
   */
  const adoptRteImages = (dialog) => {
    const rte = dialog.querySelector('[data-pp-sizechart-rte]');
    if (!rte) return;
    // Idempotent: the variant picker re-renders through the Section Rendering API, and a shopper
    // can open the dialog many times per visit. A re-render brings a FRESH node with no flag on it,
    // which is exactly right — the new markup does need adopting again.
    if (rte.dataset.ppZoomReady === 'true') return;
    // Keep page-authored chart images out of the product's initial download queue.
    const content = rte.querySelector('template[data-pp-sizechart-content]');
    if (content) content.replaceWith(content.content.cloneNode(true));
    rte.dataset.ppZoomReady = 'true';

    const hint = dialog.querySelector('.pp-scmodal__zoomhint');
    let adopted = 0;

    rte.querySelectorAll('img').forEach((img) => {
      // A chart the merchant already linked (usually to the full-size file) stays a link: a button
      // nested inside an anchor is invalid HTML and gives two conflicting things to activate.
      if (img.closest('a') || img.closest('.pp-scmodal__zoom')) return;

      const btn = document.createElement('button');
      // NOT optional. This dialog is rendered inside the variant picker's <form>, so a button
      // without it defaults to submit — the same trap that once made the close X submit the form.
      btn.type = 'button';
      btn.className = 'pp-scmodal__zoom';
      btn.setAttribute('aria-pressed', 'false');
      if (hint && hint.id) btn.setAttribute('aria-describedby', hint.id);
      // A button whose only content is an image takes that image's alt as its accessible name, and
      // the real charts here are pasted in with alt="" — that would ship a nameless button. Fall
      // back to the translated label Liquid put on the wrapper.
      if (!img.getAttribute('alt')) btn.setAttribute('aria-label', rte.dataset.zoomLabel || '');

      // Same class the Liquid path uses, so the fit-to-width, is-zoomed and cursor rules in
      // pp-pdp.css apply without a single new selector.
      img.classList.add('pp-scmodal__img');
      // replaceWith drops the button where the image was; the image is still held by this variable,
      // so appending it moves the live node — no clone, no lost decoding or cached bytes.
      img.replaceWith(btn);
      btn.appendChild(img);
      adopted += 1;
    });

    // Only promise a zoom that exists. A chart stored as a real HTML table adopts nothing and keeps
    // the hint hidden. `hidden` alone is not enough here: .pp-scmodal__zoomhint sets display:block,
    // which outranks the UA rule for [hidden] — pp-pdp.css carries an explicit [hidden] rule to win
    // that fight back. Removing it makes the hint show even with no image to zoom.
    if (adopted > 0 && hint) hint.hidden = false;
  };

  const getZoomScale = (img) => {
    const scale = Number.parseFloat(img.dataset.ppZoomScale || '');
    return Number.isFinite(scale) ? clamp(scale, MIN_ZOOM, MAX_ZOOM) : MIN_ZOOM;
  };

  // A measurements chart is functional content, so legibility wins over pixel-perfect rendering.
  // Picasa-style viewers intentionally allow magnification beyond 1 image pixel per CSS pixel: the
  // last steps can look softer, but a shopper can still make a tiny number large enough to read.
  const getMaxZoomScale = () => MAX_ZOOM;

  const isDesktopImageViewer = (img) =>
    desktopViewer.matches && img.closest('.pp-scmodal')?.classList.contains('is-image-viewer');

  const getPan = (img, axis) => {
    const value = Number.parseFloat(img.dataset[axis === 'x' ? 'ppZoomPanX' : 'ppZoomPanY'] || '');
    return Number.isFinite(value) ? value : 0;
  };

  const getFittedSize = (img, scale = getZoomScale(img)) => {
    const rect = img.getBoundingClientRect();
    return {
      width: scale > 0 ? rect.width / scale : rect.width,
      height: scale > 0 ? rect.height / scale : rect.height,
    };
  };

  const clampDesktopPan = (img, scale, panX, panY, fittedSize = getFittedSize(img)) => {
    const body = img.closest('.pp-scmodal__body');
    if (!body) return { x: 0, y: 0 };

    const maxX = Math.max(0, (fittedSize.width * scale - body.clientWidth) / 2);
    const maxY = Math.max(0, (fittedSize.height * scale - body.clientHeight) / 2);
    return {
      x: clamp(panX, -maxX, maxX),
      y: clamp(panY, -maxY, maxY),
    };
  };

  const setDesktopPan = (img, scale, panX, panY, fittedSize) => {
    const pan = clampDesktopPan(img, scale, panX, panY, fittedSize);
    img.dataset.ppZoomPanX = String(pan.x);
    img.dataset.ppZoomPanY = String(pan.y);
    img.style.setProperty('--pp-scmodal-pan-x', `${pan.x}px`);
    img.style.setProperty('--pp-scmodal-pan-y', `${pan.y}px`);
    return pan;
  };

  const syncZoomControls = (dialog, img) => {
    if (!dialog || !img) return;
    activeZoomImages.set(dialog, img);

    const controls = dialog.querySelector('[data-pp-sizechart-controls]');
    if (!controls) return;

    const scale = getZoomScale(img);
    const fittedWidth = getFittedSize(img, scale).width;
    const maxScale = getMaxZoomScale(img, fittedWidth);
    const range = controls.querySelector('[data-pp-sizechart-zoom-range]');
    const value = controls.querySelector('[data-pp-sizechart-zoom-value]');
    const zoomOut = controls.querySelector('[data-pp-sizechart-zoom-action="out"]');
    const zoomIn = controls.querySelector('[data-pp-sizechart-zoom-action="in"]');
    const reset = controls.querySelector('[data-pp-sizechart-zoom-action="reset"]');

    if (range) {
      range.max = String(Math.max(100, Math.round(maxScale * 100)));
      range.value = String(Math.round(scale * 100));
    }
    if (value) value.textContent = `${Math.round(scale * 100)}%`;
    if (zoomOut) zoomOut.disabled = scale <= MIN_ZOOM + ZOOM_EPSILON;
    if (zoomIn) zoomIn.disabled = scale >= maxScale - ZOOM_EPSILON;
    if (reset) reset.disabled = scale <= MIN_ZOOM + ZOOM_EPSILON;
  };

  const clearZoomStyles = (img) => {
    img.style.removeProperty('--pp-scmodal-zoom-width');
    img.style.removeProperty('--pp-scmodal-zoom-scale');
    img.style.removeProperty('--pp-scmodal-pan-x');
    img.style.removeProperty('--pp-scmodal-pan-y');
    delete img.dataset.ppZoomScale;
    delete img.dataset.ppZoomBaseWidth;
    delete img.dataset.ppZoomPanX;
    delete img.dataset.ppZoomPanY;
    img.classList.remove('is-zoomed');
    img.closest('.pp-scmodal__zoom')?.setAttribute('aria-pressed', 'false');
  };

  /* One scale setter serves wheel, pointer, touch and keyboard input. Keeping all four paths here
   * prevents a stale inline width from surviving a click-to-reset after the shopper used the
   * wheel. Pointer/touch activation centres the chosen part of the chart, matching the original
   * tap behaviour; wheel input keeps the number beneath the pointer stationary. */
  const applyZoomScale = (img, requestedScale, options = {}) => {
    const zoomBtn = img.closest('.pp-scmodal__zoom');
    const body = img.closest('.pp-scmodal__body');
    const dialog = img.closest('.pp-scmodal');
    if (!zoomBtn) return false;

    const currentScale = getZoomScale(img);
    const rect = img.getBoundingClientRect();
    const hasPoint = Number.isFinite(options.clientX) && Number.isFinite(options.clientY);
    const atX = hasPoint && rect.width > 0 ? clamp((options.clientX - rect.left) / rect.width, 0, 1) : 0.5;
    const atY = hasPoint && rect.height > 0 ? clamp((options.clientY - rect.top) / rect.height, 0, 1) : 0.5;

    // Keep the original fit width stable while zooming. Once a vertical scrollbar appears on
    // Windows the button can become ~17px narrower; re-reading clientWidth on every wheel tick
    // would then make the picture shrink even though the requested scale increased.
    let fittedWidth = Number.parseFloat(img.dataset.ppZoomBaseWidth || '');
    if (!Number.isFinite(fittedWidth) || fittedWidth <= 0) {
      fittedWidth = currentScale > MIN_ZOOM + ZOOM_EPSILON ? rect.width / currentScale : rect.width;
    }
    if (fittedWidth <= 0) return false;

    const nextScale = clamp(requestedScale, MIN_ZOOM, getMaxZoomScale(img, fittedWidth));
    if (Math.abs(nextScale - currentScale) < ZOOM_EPSILON) return false;

    if (nextScale <= MIN_ZOOM + ZOOM_EPSILON) {
      clearZoomStyles(img);
      if (body) {
        body.scrollLeft = 0;
        if (isDesktopImageViewer(img)) body.scrollTop = 0;
      }
      syncZoomControls(dialog, img);
      return true;
    }

    img.dataset.ppZoomBaseWidth = String(fittedWidth);
    img.dataset.ppZoomScale = String(nextScale);
    img.classList.add('is-zoomed');
    zoomBtn.setAttribute('aria-pressed', 'true');

    if (isDesktopImageViewer(img) && body) {
      const fittedSize = {
        width: fittedWidth,
        height: currentScale > 0 ? rect.height / currentScale : rect.height,
      };
      const bodyRect = body.getBoundingClientRect();
      const oldPanX = getPan(img, 'x');
      const oldPanY = getPan(img, 'y');
      const pointX = hasPoint ? options.clientX - (bodyRect.left + body.clientWidth / 2) : 0;
      const pointY = hasPoint ? options.clientY - (bodyRect.top + body.clientHeight / 2) : 0;
      const imagePointX = (pointX - oldPanX) / currentScale;
      const imagePointY = (pointY - oldPanY) / currentScale;
      const targetX = options.center ? 0 : pointX;
      const targetY = options.center ? 0 : pointY;

      img.style.setProperty('--pp-scmodal-zoom-scale', String(nextScale));
      setDesktopPan(
        img,
        nextScale,
        targetX - imagePointX * nextScale,
        targetY - imagePointY * nextScale,
        fittedSize
      );
      body.scrollLeft = 0;
      body.scrollTop = 0;
      syncZoomControls(dialog, img);
      return true;
    }

    img.style.setProperty('--pp-scmodal-zoom-width', `${fittedWidth * nextScale}px`);

    if (body) {
      const grown = img.getBoundingClientRect();
      const bodyRect = body.getBoundingClientRect();
      const targetX = options.center
        ? bodyRect.left + body.clientWidth / 2
        : hasPoint
          ? options.clientX
          : bodyRect.left + body.clientWidth / 2;
      const targetY = options.center
        ? bodyRect.top + body.clientHeight / 2
        : hasPoint
          ? options.clientY
          : bodyRect.top + body.clientHeight / 2;
      body.scrollLeft += grown.left + atX * grown.width - targetX;
      body.scrollTop += grown.top + atY * grown.height - targetY;
    }

    syncZoomControls(dialog, img);
    return true;
  };

  const resetDialogZoom = (dialog) => {
    dialog.querySelectorAll('.pp-scmodal__img').forEach((img) => {
      clearZoomStyles(img);
    });

    const body = dialog.querySelector('.pp-scmodal__body');
    if (body) {
      body.scrollLeft = 0;
      body.scrollTop = 0;
      body.classList.remove('is-panning');
    }

    const firstImage = dialog.querySelector('.pp-scmodal__img');
    if (firstImage) syncZoomControls(dialog, firstImage);
  };

  const onWheelZoom = (event) => {
    // Preserve browser page zoom and the desktop convention that Shift+wheel pans horizontally.
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.deltaY === 0) return;

    const zoomBtn = event.currentTarget;
    const img = zoomBtn.querySelector('.pp-scmodal__img');
    const dialog = zoomBtn.closest('.pp-scmodal');
    if (!img || !dialog?.open) return;

    let delta = event.deltaY;
    if (event.deltaMode === 1) delta *= 16;
    if (event.deltaMode === 2) {
      const body = img.closest('.pp-scmodal__body');
      delta *= body?.clientHeight || window.innerHeight;
    }
    delta = clamp(delta, -120, 120);

    const currentScale = getZoomScale(img);
    const nextScale = clamp(currentScale * Math.exp(-delta * ZOOM_SPEED), MIN_ZOOM, MAX_ZOOM);
    const changed = applyZoomScale(img, nextScale, {
      clientX: event.clientX,
      clientY: event.clientY,
    });

    // Do not trap the scroll chain at either zoom boundary. The page/dialog may continue scrolling
    // whenever this wheel tick cannot change the chart scale.
    if (changed) event.preventDefault();
  };

  const finishPan = (zoomBtn, event, suppressClick) => {
    const state = dragStates.get(zoomBtn);
    if (!state || (event && state.pointerId !== event.pointerId)) return;

    dragStates.delete(zoomBtn);
    zoomBtn.closest('.pp-scmodal__body')?.classList.remove('is-panning');
    if (event && zoomBtn.hasPointerCapture?.(event.pointerId)) {
      zoomBtn.releasePointerCapture(event.pointerId);
    }
    if (suppressClick && state.moved) suppressedZoomClicks.add(zoomBtn);
  };

  const onPanStart = (event) => {
    const zoomBtn = event.currentTarget;
    const img = zoomBtn.querySelector('.pp-scmodal__img');
    if (
      !img ||
      !isDesktopImageViewer(img) ||
      getZoomScale(img) <= MIN_ZOOM + ZOOM_EPSILON ||
      event.button !== 0 ||
      event.isPrimary === false
    ) {
      return;
    }

    dragStates.set(zoomBtn, {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panX: getPan(img, 'x'),
      panY: getPan(img, 'y'),
      moved: false,
    });
    zoomBtn.setPointerCapture?.(event.pointerId);
    zoomBtn.closest('.pp-scmodal__body')?.classList.add('is-panning');
  };

  const onPanMove = (event) => {
    const zoomBtn = event.currentTarget;
    const state = dragStates.get(zoomBtn);
    const img = zoomBtn.querySelector('.pp-scmodal__img');
    if (!state || !img || state.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - state.startX;
    const deltaY = event.clientY - state.startY;
    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) state.moved = true;
    setDesktopPan(img, getZoomScale(img), state.panX + deltaX, state.panY + deltaY);
    event.preventDefault();
  };

  const onPanEnd = (event) => finishPan(event.currentTarget, event, true);
  const onPanCancel = (event) => finishPan(event.currentTarget, event, false);

  const getControlImage = (dialog) =>
    activeZoomImages.get(dialog) || dialog.querySelector('.pp-scmodal__img');

  const prepareZoom = (dialog) => {
    const zoomButtons = dialog.querySelectorAll('.pp-scmodal__zoom');
    const controls = dialog.querySelector('[data-pp-sizechart-controls]');
    const range = controls?.querySelector('[data-pp-sizechart-zoom-range]');
    const rte = dialog.querySelector('[data-pp-sizechart-rte]');
    // A full-window canvas has one active subject. Keep the old scrollable layout when a merchant
    // page mixes explanatory text or multiple pictures, otherwise absolute viewer layers would
    // overlap content the shopper still needs to read.
    const canUseImageViewer = zoomButtons.length === 1 && (!rte || rte.textContent.trim() === '');
    dialog.classList.toggle('is-image-viewer', canUseImageViewer);
    if (controls) controls.hidden = !canUseImageViewer;

    if (range && !preparedZoomRanges.has(range)) {
      preparedZoomRanges.add(range);
      // The dialog lives inside Horizon's variant form. Letting this range input bubble makes the
      // product picker mistake a zoom change for a variant change and morph the open dialog back to
      // its server markup. Keep both input and change local to the viewer.
      range.addEventListener('input', (event) => {
        event.stopPropagation();
        onZoomRangeInput(event);
      });
      range.addEventListener('change', (event) => event.stopPropagation());
    }

    zoomButtons.forEach((zoomBtn) => {
      // Morph-based variant updates can remove runtime data attributes while retaining the same
      // DOM node and its listeners. A WeakSet follows node identity instead, so reopening after a
      // variant change never multiplies the wheel handler.
      if (preparedZoomButtons.has(zoomBtn)) return;
      preparedZoomButtons.add(zoomBtn);
      zoomBtn.addEventListener('wheel', onWheelZoom, { passive: false });
      zoomBtn.addEventListener('pointerdown', onPanStart);
      zoomBtn.addEventListener('pointermove', onPanMove);
      zoomBtn.addEventListener('pointerup', onPanEnd);
      zoomBtn.addEventListener('pointercancel', onPanCancel);
      zoomBtn.addEventListener('lostpointercapture', (event) => finishPan(zoomBtn, event, true));
      zoomBtn.addEventListener('dragstart', (event) => event.preventDefault());

      const img = zoomBtn.querySelector('.pp-scmodal__img');
      if (img && !img.complete) {
        img.addEventListener('load', () => syncZoomControls(dialog, img), { once: true });
      }
    });

    if (!preparedZoomDialogs.has(dialog)) {
      preparedZoomDialogs.add(dialog);
      dialog.addEventListener('close', () => resetDialogZoom(dialog));
    }
  };

  const onZoomControlClick = (event) => {
    const control = event.target.closest('[data-pp-sizechart-zoom-action]');
    if (!control) return;

    const dialog = control.closest('.pp-scmodal');
    const img = dialog ? getControlImage(dialog) : null;
    if (!img) return;

    const currentScale = getZoomScale(img);
    const action = control.dataset.ppSizechartZoomAction;
    if (action === 'reset') {
      applyZoomScale(img, MIN_ZOOM);
    } else if (action === 'in') {
      applyZoomScale(img, currentScale * ZOOM_STEP);
    } else if (action === 'out') {
      applyZoomScale(img, currentScale / ZOOM_STEP);
    }
  };

  const onZoomRangeInput = (event) => {
    const range = event.target.closest('[data-pp-sizechart-zoom-range]');
    if (!range) return;

    const dialog = range.closest('.pp-scmodal');
    const img = dialog ? getControlImage(dialog) : null;
    const scale = Number.parseFloat(range.value) / 100;
    if (img && Number.isFinite(scale)) applyZoomScale(img, scale);
  };

  // A cached pixel base is intentionally stable while the internal scrollbar appears, but it is
  // no longer the fitted width after a real viewport-width change (window resize, browser zoom or
  // phone rotation). Return open charts to fit so the next wheel/tap starts from the new width.
  let viewportWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    const nextViewportWidth = window.innerWidth;
    if (Math.abs(nextViewportWidth - viewportWidth) < 1) return;
    viewportWidth = nextViewportWidth;
    document.querySelectorAll('dialog.pp-scmodal[open]').forEach(resetDialogZoom);
  });

  const onClick = (event) => {
    // Close button first: it lives inside the dialog, so it can never also be an open trigger.
    // The markup used to try type="submit" formmethod="dialog", which only closes a dialog when
    // the submitting form is inside it — here the dialog sits within the variant picker's form,
    // so the X did nothing and Escape was the only way out.
    const closer = event.target.closest('[data-pp-sizechart-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    const trigger = event.target.closest('[data-pp-sizechart-open]');
    if (!trigger) return;

    // Let the browser do its normal thing for open-in-new-tab intents.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

    const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
    // No dialog on the page means the guide is an external URL we cannot embed — follow the link.
    if (!dialog || typeof dialog.showModal !== 'function') return;

    event.preventDefault();
    // Before showing, not after: the button wrapper changes the layout box the image sits in, and
    // doing it on an already-visible dialog would show the chart reflowing under the shopper.
    adoptRteImages(dialog);
    prepareZoom(dialog);
    resetDialogZoom(dialog);
    dialog.showModal();
    const firstImage = dialog.querySelector('.pp-scmodal__img');
    if (firstImage) syncZoomControls(dialog, firstImage);
  };

  // Clicking the backdrop closes it. The dialog element covers the whole viewport, so a click that
  // lands on the element itself (rather than on its content box) is a backdrop click.
  const onDialogClick = (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    const box = dialog.getBoundingClientRect();
    const outside =
      event.clientX < box.left ||
      event.clientX > box.right ||
      event.clientY < box.top ||
      event.clientY > box.bottom;
    if (outside) dialog.close();
  };


  /* Tap/click the chart to switch between fit-to-width and up to 5×.
     A size chart is numbers drawn as a
     picture: at phone width its figures render around 6px tall, which is present and unreadable —
     the same as absent for the question the shopper opened it to answer. Keeping the selected point
     centred matters as much as the zoom itself: without it, tapping the L column jumps you to the
     top-left of the image and you have to hunt for the row you were already looking at. */
  const onZoom = (event) => {
    // The picture sits inside a real <button> so the zoom can be reached and fired from a keyboard.
    // That means two shapes of the same event: a pointer click reports the IMAGE as its target,
    // Enter/Space on the button reports the BUTTON. Look for either.
    const zoomBtn = event.target.closest('.pp-scmodal__zoom');
    const img = zoomBtn ? zoomBtn.querySelector('.pp-scmodal__img') : event.target.closest('.pp-scmodal__img');
    if (!img) return;

    if (zoomBtn && suppressedZoomClicks.has(zoomBtn)) {
      suppressedZoomClicks.delete(zoomBtn);
      return;
    }

    // Enter/Space synthesise a click carrying no real coordinates (detail === 0 in every browser;
    // clientX/Y are 0). Treating that as "the shopper pointed at the top-left corner" would throw a
    // keyboard user to a corner of the table they were not reading, so aim at the middle instead.
    const fromKeyboard = event.detail === 0;
    const currentScale = getZoomScale(img);
    applyZoomScale(img, currentScale > MIN_ZOOM + ZOOM_EPSILON ? MIN_ZOOM : MAX_ZOOM, {
      clientX: fromKeyboard ? undefined : event.clientX,
      clientY: fromKeyboard ? undefined : event.clientY,
      center: true,
    });
  };

  document.addEventListener('click', onZoom);
  document.addEventListener('click', onClick);
  document.addEventListener('click', onZoomControlClick);
  document.addEventListener('click', (e) => {
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('pp-scmodal')) onDialogClick(e);
  });
})();
